<tui-loader 
  [overlay]="true" 
  [showLoader]="isLoading && isEditMode" 
  class="w-full block min-h-[400px]"
>
<section class="w-full">
  <p class="text-4xl font-normal mt-[0.9rem]">
    {{ isWalkInMode
        ? (isEditMode ? 'Edit Walk-in Patient Charge' : 'Add Walk-in Patient Charge')
        : (isEditMode ? 'Edit Procedure' : 'Add Extra Charge or Procedure') }}
  </p>
  <form [formGroup]="procedureForm">
    <!-- Patient Type (hidden in walk-in mode) -->
    <div class="flex mt-5" *ngIf="!isWalkInMode">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Client Type
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Filters the client picker below
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
        <label tuiLabel>
          <span *ngIf="hasFieldValue('clientType')" class="value-field-label">
            Client Type
          </span>
          <tui-combo-box
            formControlName="clientType"
            [stringify]="stringifyClientType"
            [tuiTextfieldLabelOutside]="true"
            placeholder="Select client type"
          >
            Client Type
            <tui-data-list-wrapper
              *tuiDataList
              [items]="selectableClientTypeOptions"
              [itemContent]="stringifyClientType | tuiStringifyContent"
            />
          </tui-combo-box>
          <tui-error
            formControlName="clientType"
            class="mb-3"
            [error]="[] | tuiFieldError | async"
          ></tui-error>
        </label>
      </div>
    </div>

    <!-- Client (hidden in walk-in mode) -->
    <div class="flex mt-5" *ngIf="!isWalkInMode">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Client
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Select the client for this charge
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
        <span *ngIf="hasFieldValue('clientID')" class="value-field-label">
          Client
        </span>
        <tui-combo-box
          formControlName="clientID"
          [stringify]="stringifyClients"
          [tuiTextfieldLabelOutside]="true"
          placeholder="Select Client"
        >
          <tui-data-list-wrapper
            *tuiDataList
            [items]="clients | tuiFilterByInput"
            [itemContent]="stringifyClients | tuiStringifyContent"
          />
          Client
        </tui-combo-box>
        <tui-error
          formControlName="clientID"
          class="mb-3"
          [error]="[] | tuiFieldError | async"
        ></tui-error>
      </div>
    </div>

    <!-- Walk-in patient name (walk-in mode only) -->
    <div class="flex mt-5" *ngIf="isWalkInMode">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Patient Name
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Enter the walk-in patient's name (no patient record is created)
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
        <label tuiLabel>
          <span *ngIf="hasFieldValue('walkInClientName')" class="value-field-label">
            Patient Name
          </span>
          <tui-input
            formControlName="walkInClientName"
            [tuiTextfieldLabelOutside]="true"
          >
            Patient Name
            <input
              tuiTextfieldLegacy
              type="text"
              placeholder="Enter walk-in patient name"
            />
          </tui-input>
          <tui-error
            formControlName="walkInClientName"
            class="mb-3"
            [error]="[] | tuiFieldError | async"
          ></tui-error>
        </label>
      </div>
    </div>

    <!-- Walk-in contact number (walk-in mode only, optional) -->
    <div class="flex mt-5" *ngIf="isWalkInMode">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Contact Number
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Optional contact number for the walk-in patient
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
        <label tuiLabel>
          <span *ngIf="hasFieldValue('walkInContactNo')" class="value-field-label">
            Contact Number
          </span>
          <tui-input
            formControlName="walkInContactNo"
            [tuiTextfieldLabelOutside]="true"
          >
            Contact Number
            <input
              tuiTextfieldLegacy
              type="text"
              placeholder="Enter contact number (optional)"
            />
          </tui-input>
        </label>
      </div>
    </div>

    <!-- Procedures -->
    <div class="flex mt-5">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Procedures &amp; Services
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Select one or more procedures
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3 flex flex-col gap-3">
        <label tuiLabel>
          <span *ngIf="hasFieldValue('procedures')" class="value-field-label">
            Procedures &amp; Services
          </span>
          <tui-multi-select
            formControlName="procedures"
            [stringify]="stringifyProcedure"
            [tuiTextfieldLabelOutside]="true"
            placeholder="Select procedure(s)"
          >
            Procedures &amp; Services
            <tui-data-list-wrapper
              *tuiDataList
              [items]="procedureOptions"
              [itemContent]="stringifyProcedure | tuiStringifyContent"
            />
          </tui-multi-select>
        </label>
        <div
          *ngIf="
            procedureForm.get('procedures')?.invalid &&
            procedureForm.get('procedures')?.touched
          "
          class="text-red-500 text-sm -mt-1"
        >
          At least one procedure is required
        </div>

        <div
          *ngIf="showAddNewProcedure"
          class="my-4 p-4 border border-[#e5e7ea] rounded-lg bg-[#fafafa]"
        >
          <div class="flex items-end gap-2 flex-wrap">
            <div class="flex-1 min-w-[200px]">
              <label tuiLabel>
                <span *ngIf="newProcedure" class="value-field-label">
                  Add New Procedure
                </span>
                <tui-input
                  [(ngModel)]="newProcedure"
                  [ngModelOptions]="{ standalone: true }"
                  (keydown.enter)="addNewProcedure()"
                  [tuiTextfieldLabelOutside]="true"
                >
                  Add New Procedure
                  <input
                    tuiTextfieldLegacy
                    type="text"
                    placeholder="Enter custom procedure name"
                  />
                </tui-input>
              </label>
            </div>
            <button
              tuiButton
              type="button"
              (click)="addNewProcedure()"
              class="px-4 py-2"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Address -->
    <div class="flex mt-5">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Address
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Optional. Useful for Inward / Non Registered clients without a
          client address on file.
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
        <label tuiLabel>
          <span *ngIf="hasFieldValue('address')" class="value-field-label">
            Address
          </span>
          <tui-textarea
            formControlName="address"
            [tuiTextfieldLabelOutside]="true"
            placeholder="Enter address (optional)"
          >
            Address
          </tui-textarea>
        </label>
      </div>
    </div>

    <!-- Care team -->
    <div class="flex mt-5">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Care team
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Doctor and senior nurse details
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3 flex flex-col gap-3">
        <label tuiLabel>
          <span *ngIf="hasFieldValue('doctorName')" class="value-field-label">
            Doctor Name
          </span>
          <tui-input
            formControlName="doctorName"
            [tuiTextfieldLabelOutside]="true"
          >
            Doctor Name
            <input
              tuiTextfieldLegacy
              type="text"
              placeholder="Enter doctor name"
            />
          </tui-input>
        </label>

        <label tuiLabel>
          <span *ngIf="hasFieldValue('nurseName')" class="value-field-label">
            Senior Nurse Name
          </span>
          <tui-input
            formControlName="nurseName"
            [tuiTextfieldLabelOutside]="true"
          >
            Senior Nurse Name
            <input
              tuiTextfieldLegacy
              type="text"
              placeholder="Enter senior nurse name"
            />
          </tui-input>
          <tui-error
            formControlName="nurseName"
            class="mb-3"
            [error]="[] | tuiFieldError | async"
          ></tui-error>
        </label>
      </div>
    </div>

    <!-- Charges -->
    <div class="flex mt-5" formGroupName="payments">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Charges
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Enter amounts in LKR (optional where empty)
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3 flex flex-col gap-3">
        <div class="value-field">
          <label *ngIf="hasFieldValue('payments.doctorPayment')" class="value-field-label">Doctor Charges (LKR)</label>
          <tui-input
            formControlName="doctorPayment"
            type="number"
            [tuiTextfieldLabelOutside]="true"
          >
            Doctor Charges (LKR)
            <input
              tuiTextfieldLegacy
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
            />
          </tui-input>
        </div>

        <div class="value-field">
          <label *ngIf="hasFieldValue('payments.nursingPayment')" class="value-field-label">Nursing Charges (LKR)</label>
          <tui-input
            formControlName="nursingPayment"
            type="number"
            [tuiTextfieldLabelOutside]="true"
          >
            Nursing Charges (LKR)
            <input
              tuiTextfieldLegacy
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
            />
          </tui-input>
        </div>

        <div class="value-field">
          <label *ngIf="hasFieldValue('payments.pharmacyPayment')" class="value-field-label">Pharmacy Charges (LKR)</label>
          <tui-input
            formControlName="pharmacyPayment"
            type="number"
            [tuiTextfieldLabelOutside]="true"
          >
            Pharmacy Charges (LKR)
            <input
              tuiTextfieldLegacy
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
            />
          </tui-input>
        </div>

        <div class="value-field">
          <label *ngIf="hasFieldValue('payments.laboratoryPayment')" class="value-field-label">Laboratory Charges (LKR)</label>
          <tui-input
            formControlName="laboratoryPayment"
            type="number"
            [tuiTextfieldLabelOutside]="true"
          >
            Laboratory Charges (LKR)
            <input
              tuiTextfieldLegacy
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
            />
          </tui-input>
        </div>

        <div class="value-field">
          <label *ngIf="hasFieldValue('payments.ambulancePayment')" class="value-field-label">Ambulance Charges (LKR)</label>
          <tui-input
            formControlName="ambulancePayment"
            type="number"
            [tuiTextfieldLabelOutside]="true"
          >
            Ambulance Charges (LKR)
            <input
              tuiTextfieldLegacy
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
            />
          </tui-input>
        </div>

        <div class="value-field">
          <label *ngIf="hasFieldValue('payments.physiotherapyPayment')" class="value-field-label">Physiotherapy Charges (LKR)</label>
          <tui-input
            formControlName="physiotherapyPayment"
            type="number"
            [tuiTextfieldLabelOutside]="true"
          >
            Physiotherapy Charges (LKR)
            <input
              tuiTextfieldLegacy
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
            />
          </tui-input>
        </div>

        <div class="value-field">
          <label *ngIf="hasFieldValue('payments.otherPayment')" class="value-field-label">Other Charges (LKR)</label>
          <tui-input
            formControlName="otherPayment"
            type="number"
            [tuiTextfieldLabelOutside]="true"
          >
            Other Charges (LKR)
            <input
              tuiTextfieldLegacy
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
            />
          </tui-input>
        </div>
      </div>
    </div>

    <!-- Custom payment -->
    <div class="flex mt-5">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Custom payment
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          Add named line items
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
        <div
          class="mb-4 p-4 border border-[#e5e7ea] rounded-lg bg-[#fafafa]"
        >
          <div class="flex items-end gap-2 flex-wrap">
            <div class="flex-1 min-w-[160px]">
              <label tuiLabel>
                <span *ngIf="newPaymentName" class="value-field-label">
                  Custom charge name
                </span>
                <tui-input
                  [(ngModel)]="newPaymentName"
                  [ngModelOptions]="{ standalone: true }"
                  [tuiTextfieldLabelOutside]="true"
                >
                  Custom charge name
                  <input
                    tuiTextfieldLegacy
                    type="text"
                    placeholder="e.g. X-Ray"
                  />
                </tui-input>
              </label>
            </div>
            <div class="w-full sm:w-32">
              <label tuiLabel>
                <span *ngIf="hasStandaloneValue(newPaymentValue)" class="value-field-label">
                  Amount (LKR)
                </span>
                <tui-input
                  [(ngModel)]="newPaymentValue"
                  [ngModelOptions]="{ standalone: true }"
                  type="number"
                  [tuiTextfieldLabelOutside]="true"
                >
                  Amount (LKR)
                  <input
                    tuiTextfieldLegacy
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0"
                  />
                </tui-input>
              </label>
            </div>
            <button tuiButton type="button" (click)="addCustomPayment()">
              Add
            </button>
          </div>
        </div>

        <div class="mb-4 p-3 bg-[#f3f4f6] rounded-lg">
          <div class="text-lg font-semibold text-[#1E293B]">
            Grand Total: LKR
            {{ procedureForm.get('grandTotal')?.value | number : '1.2-2' }}
          </div>
        </div>

        <div *ngIf="customPayments.length > 0" class="mb-4">
          <p class="text-base font-medium text-[#3E4A58] mb-2">Added items</p>
          <div class="flex flex-wrap gap-2">
            <span
              *ngFor="let customPayment of customPayments; let i = index"
              class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#e0e7ff] text-[#3730a3]"
            >
              {{ customPayment.name }}: LKR
              {{ customPayment.value | number : '1.2-2' }}
              <button
                type="button"
                (click)="removeCustomPayment(i)"
                class="ml-2 text-[#4338ca] hover:text-[#312e81] border-none bg-transparent p-0 cursor-pointer"
              >
                ×
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Date -->
    <div class="flex mt-5">
      <div>
        <p class="text-base font-medium text-[#3E4A58] my-0 w-[155px]">
          Service date
        </p>
        <p class="text-sm font-normal text-[#949494D9] my-0 w-[155px]">
          When the service was provided
        </p>
      </div>
      <div class="pl-[167px] w-full xl:w-1/3 2xl:w-1/3">
        <label tuiLabel>
          <span *ngIf="hasFieldValue('date')" class="value-field-label">
            Date
          </span>
          <tui-input-date
            formControlName="date"
            [tuiTextfieldLabelOutside]="true"
          >
            Date
            <input tuiTextfieldLegacy placeholder="Select date" />
          </tui-input-date>
          <tui-error
            formControlName="date"
            class="mb-3"
            [error]="[] | tuiFieldError | async"
          ></tui-error>
        </label>
      </div>
    </div>

    <div class="mt-5 flex justify-end pl-[167px]">
      <button
        appearance="primary"
        tuiButton
        type="submit"
        class="w-[200px]"
        title="Submit"
        (click)="submit()"
        [disabled]="isSaving || isLoading"
        [appDisableUnlessPermission]="
          isEditMode ? 'EXTRA_CHARGES_UPDATE' : 'EXTRA_CHARGES_CREATE'
        "
      >
        <ng-container *ngIf="!isSaving">
          {{ isEditMode ? 'Update Procedure' : 'Submit Procedure' }}
        </ng-container>

        <ng-container *ngIf="isSaving">
          {{ isEditMode ? 'Updating...' : 'Saving...' }}
        </ng-container>
      </button>
    </div>
  </form>
</section>
</tui-loader>
